import { FormEvent, useCallback } from 'react';
import styled from 'styled-components';

import { noop } from 'lodash';
import { useQuoteBuilderView } from '@paradigm/features/src/unified-rfqs/ui/drfq/quote-builder/view';
import {
  QuoteSubmitReport,
  useQuoteBuilderSubmit,
} from '@paradigm/features/src/unified-rfqs/ui/drfq/quote-builder/submit-context';

import { darkerBlackPearl } from '@paradigm/design-system/src/colors';
import {
  useQuoteBuilderModalMethods,
  useQuoteBuilderModalState,
} from '@paradigm/features/src/unified-rfqs/ui/drfq/quote-builder/modal-context';
import {
  isValidDecimal,
  parseFormattedFloat,
} from '@paradigm/utils/formatDecimal';
import { useDrfqMakerOrders } from '@paradigm/features/src/unified-rfqs/ui/drfq/drfq-maker-orderbook-context';
import { toast } from '#/components/UnifiedRfqs/toast';
import { useSubmitted } from '@paradigm/features/src/forms/submitted-context';
import { AccountSelectContainer } from '#/components/account-selection/AccountSelect';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import { useUnifiedMarketsV2PricingAlgorithmFeatureFlag } from '@paradigm/features/src/feature-flags/context';
import PricingOptionsContainer from '#/components/UnifiedRfqs/components/PricingOptionsContainer';
import Button from '@paradigm/design-system/src/components/Button';
import useKeybinding from '#/hooks/useKeybinding';
import { useTrackAction } from '@paradigm/features/src/analytics';
import { useEstimatedPriceModalMethods } from '@paradigm/features/src/unified-rfqs/ui/drfq/quote-builder/estimated-price-modal-context';
import { Divider } from '../common';
import AccountSelect from './AccountSelect';
import TwoWay, { TwoWayContainer } from './TwoWay';
import EstimatedPriceModal from './EstimatedPriceModal';

export default function QuoteBuilderForm({
  greeks,
}: {
  greeks: JSX.Element | null;
}) {
  const { isSingleLeg, isValid, price } = useQuoteBuilderView();
  const { legPricingEnabled } = useQuoteBuilderModalState();
  const { close, setLegPricing, setIsMarkPricePrefill } =
    useQuoteBuilderModalMethods();
  const { submitQuote, isLoading } = useQuoteBuilderSubmit();
  const estimatedPriceModal = useEstimatedPriceModalMethods();
  const { bid, ask } = useDrfqMakerOrders();
  const { setSubmit } = useSubmitted();
  const isV2PricingAlgoEnabled =
    useUnifiedMarketsV2PricingAlgorithmFeatureFlag();

  const [rawBid, rawAsk] = price.value;
  const bidPrice = isValidDecimal(rawBid)
    ? parseFormattedFloat(rawBid).toString()
    : undefined;
  const askPrice = isValidDecimal(rawAsk)
    ? parseFormattedFloat(rawAsk).toString()
    : undefined;

  const shouldSubmit =
    (bidPrice !== undefined && bid?.price !== bidPrice) ||
    (askPrice !== undefined && ask?.price !== askPrice);

  const trackAction = useTrackAction();
  const handleSubmit = useCallback(
    (event?: FormEvent<HTMLFormElement>) => {
      trackAction('submit quote');
      event?.preventDefault();
      setSubmit(true);
      if (!isValid) return;

      if (bidPrice == null && askPrice == null) {
        toast('You must submit a valid bid or ask price.');
        return;
      }

      if (!shouldSubmit) {
        close();
        return;
      }

      const onSubmitQuote = ({
        errors,
        estimatedPricesResp,
      }: QuoteSubmitReport) => {
        errors?.forEach((error) => toast(error));

        if (estimatedPricesResp != null) {
          estimatedPriceModal.open({ estimatedPricesResp });
          return;
        }
        if (errors?.length === 0) {
          close();
        }
      };
      submitQuote(onSubmitQuote);
    },
    [
      trackAction,
      setSubmit,
      isValid,
      bidPrice,
      askPrice,
      shouldSubmit,
      submitQuote,
      close,
      estimatedPriceModal,
    ],
  );

  const hasGreeks = greeks != null;
  const greeksDivider = hasGreeks && <Divider />;
  const toggleLegPricing = useCallback(() => {
    trackAction('toggle leg pricing', {
      isLegPricingEnabled: !legPricingEnabled,
    });
    if (!isV2PricingAlgoEnabled || isSingleLeg) return;
    setLegPricing(!legPricingEnabled);
    setIsMarkPricePrefill(false);
  }, [
    isSingleLeg,
    isV2PricingAlgoEnabled,
    legPricingEnabled,
    setIsMarkPricePrefill,
    setLegPricing,
    trackAction,
  ]);

  useKeybinding('Enter', () => handleSubmit(), noop);

  return (
    <Form onSubmit={handleSubmit}>
      <PricingOptionsContainer>
        <Button
          type="button"
          size="small"
          ariaLabel="select leg prices mode"
          variant={legPricingEnabled ? 'pill' : 'link'}
          onClick={toggleLegPricing}
          disabled={isSingleLeg}
        >
          Leg Prices
        </Button>
        <Button
          type="button"
          size="small"
          ariaLabel="select two-way mode"
          variant={!legPricingEnabled ? 'pill' : 'link'}
          onClick={toggleLegPricing}
        >
          Two-Way
        </Button>
      </PricingOptionsContainer>
      <AccountSelect />
      {greeks}
      {greeksDivider}
      <TwoWay />
      <div css="text-align:right">
        <SubmitButton
          ariaLabel="submit quote"
          type="submit"
          isValid={isValid}
          isLoading={isLoading}
        >
          Submit Quote
        </SubmitButton>
      </div>
      <EstimatedPriceModal />
    </Form>
  );
}

const Form = styled.form`
  height: 100%;
  display: grid;
  flex-direction: column;
  grid-template-rows: 1.5rem 1fr;
  gap: 1rem;

  ${AccountSelectContainer} {
    margin-bottom: 0;
  }

  ${TwoWayContainer} {
    margin: 0;
  }

  ${Divider} {
    width: 100%;
    margin-bottom: 1rem;

    &:before {
      background: linear-gradient(
        359deg,
        rgba(54, 121, 221, 0) 0%,
        rgba(54, 121, 221, 1) 50%,
        rgba(54, 121, 221, 0) 100%
      );
    }
  }

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    background-color: ${darkerBlackPearl};
    padding: 1rem;
    border-radius: 0.25rem;

    &&& button {
      font-size: 1rem;
      line-height: 2rem;
      height: auto;
      padding: 0;
      border-radius: 1rem;
    }

    label {
      font-size: 1rem;
      line-height: 2rem;
    }
  }
`;

const SubmitButton = styled(Button)`
  float: right;
  width: 116px;

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 132px;
    padding: 0 1rem;
  }
`;
